<template>
  <card title="TOP Clinics — paid placement">
    <div class="top-placements__now" :class="{'top-placements__now--live': isLive}">
      <div class="top-placements__now-state">
        <span class="top-placements__now-dot"></span>
        <template v-if="isLive">Shown right now</template>
        <template v-else-if="activePeriod">Paid period is running, but the clinic is not shown</template>
        <template v-else>Not shown — no confirmed period covering today</template>
      </div>
      <div v-if="activePeriod" class="top-placements__now-period">
        {{ periodLabel(activePeriod) }} · weight {{ placementWeight(activePeriod) }} · confirmed by
        {{ activePeriod.confirmed_by || '—' }}
      </div>
      <ul class="top-placements__checks">
        <li v-for="check in readiness" :key="check.label" :class="{'is-failed': !check.passed}">
          {{ check.passed ? '✓' : '✕' }} {{ check.label }}
        </li>
      </ul>
      <p class="top-placements__now-note">
        These are the clinic-wide conditions. Whether a given page shows the clinic additionally
        depends on that page: it has to serve the medical need of the page, and a before/after page
        needs a published case of that very procedure. Which languages run the block is a product
        setting, not a property of this clinic.
      </p>
    </div>

    <p class="top-placements__hint">
      A period is created unconfirmed and shows nowhere. Confirming it is the statement that the
      payment has been verified — it takes a slot (up to {{ slotLimit }} per direction, and
      {{ slotLimit }} on the homepage) and is recorded against your name. Dates are inclusive and in Kyiv time; a period ends at the start of the
      day after the last one.
    </p>
    <p class="top-placements__hint">
      Weight controls the order of eligible logos: 100 appears first, 0 last.
      The actual position can vary because each page filters clinics separately.
      Changes can take up to five minutes to appear on the site.
    </p>

    <div class="top-placements__form">
      <el-date-picker
        v-model="newPeriod"
        type="daterange"
        range-separator="—"
        start-placeholder="First day"
        end-placeholder="Last day"
        value-format="yyyy-MM-dd"
        format="dd.MM.yyyy"
        size="small"
      />
      <label class="top-placements__weight-label">
        Weight (0–100)
        <el-input-number v-model="newWeight" :min="0" :max="100" size="small"/>
      </label>
      <el-input v-model="newPaymentReference" placeholder="Payment reference" size="small"
                class="top-placements__reference"/>
      <el-checkbox v-model="newShowOnHomepage">Show on homepage</el-checkbox>
      <base-button type="primary" size="sm" :disabled="!canCreate" @click="create">
        Add period
      </base-button>
    </div>

    <el-table :data="placements" v-loading="loading" empty-text="No paid periods yet">
      <el-table-column label="Period" min-width="170">
        <template slot-scope="{ row }">{{ periodLabel(row) }}</template>
      </el-table-column>
      <el-table-column label="Status" width="120">
        <template slot-scope="{ row }">
          <el-tag :type="statusType(row.status)" size="mini" effect="dark">{{ row.status }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="Weight" width="175">
        <template slot-scope="{ row }">
          <el-input-number v-if="editingId !== null && editingId === row.placement_id" v-model="editingWeight"
                           :min="0" :max="100" size="mini"/>
          <span v-else>{{ placementWeight(row) }}</span>
        </template>
      </el-table-column>
      <el-table-column label="Directions" min-width="170">
        <template slot-scope="{ row }">
          <span v-if="isGlobal(row)" class="top-placements__all">All directions</span>
          <span v-else>{{ row.illness_kind_ids.map(directionName).join(', ') }}</span>
        </template>
      </el-table-column>
      <el-table-column label="Homepage" width="105">
        <template slot-scope="{ row }">
          <el-switch :value="row.show_on_homepage === true" size="mini"
                     :disabled="loading || row.status === 'cancelled' || !validPlacementId(row)"
                     @change="(value) => toggleHomepage(row, value)"/>
        </template>
      </el-table-column>
      <el-table-column label="Payment" prop="payment_reference" min-width="140"/>
      <el-table-column label="Confirmed by" prop="confirmed_by" min-width="170"/>
      <el-table-column label="" min-width="230">
        <template slot-scope="{ row }">
          <template v-if="editingId !== null && editingId === row.placement_id">
            <base-button type="success" size="sm" :disabled="loading || !validWeight(editingWeight)"
                         @click="saveWeight(row)">Save weight</base-button>
            <base-button type="default" size="sm" :disabled="loading" @click="cancelWeightEdit">
              Cancel
            </base-button>
          </template>
          <template v-else>
            <base-button v-if="row.status !== 'cancelled'" type="primary" size="sm"
                         :disabled="loading || editingId !== null || !validPlacementId(row)" @click="editWeight(row)">
              Edit weight
            </base-button>
            <base-button v-if="row.status === 'draft'" type="success" size="sm"
                         :disabled="loading || editingId !== null || !validPlacementId(row)" @click="act('confirm', row)">
              Confirm
            </base-button>
            <base-button v-if="row.status === 'confirmed'" type="warning" size="sm"
                         :disabled="loading || editingId !== null || !validPlacementId(row)" @click="act('pause', row)">
              Pause
            </base-button>
            <base-button v-if="row.status === 'paused'" type="success" size="sm"
                         :disabled="loading || editingId !== null || !validPlacementId(row)" @click="act('resume', row)">
              Resume
            </base-button>
            <base-button v-if="row.status !== 'cancelled'" type="danger" size="sm"
                         :disabled="loading || editingId !== null || !validPlacementId(row)" @click="act('cancel', row)">
              Cancel
            </base-button>
          </template>
        </template>
      </el-table-column>
    </el-table>

    <div class="top-directions">
      <h5 class="top-directions__title">Advertising by direction</h5>
      <p class="top-directions__hint">
        Every direction linked to the clinic (Main info → connections) carries two separate products,
        each with its own switch — green is on, grey is off.
        <b>Listing</b> is the balance-driven advertising in the listings: switching it on starts the
        campaign of that direction, switching it off pauses it; when the agency balance runs out the
        service suspends it by itself. <b>TOP</b> is the paid TOP period chosen below: no direction
        switched off means <b>all directions</b>, including ones linked later. The TOP period itself
        is started above, and the homepage has its own switch in the table.
      </p>
      <div v-if="editablePlacements.length === 0" class="top-directions__empty">
        No TOP period yet — the TOP switches work once a period is added above.
      </div>
      <template v-if="editablePlacements.length > 0">
        <div class="top-directions__period">
          <span>Period</span>
          <el-select v-model="directionsPlacementId" size="small" class="top-directions__select">
            <el-option v-for="row in editablePlacements" :key="row.placement_id" :value="row.placement_id"
                       :label="`${periodLabel(row)} · ${row.status}`"/>
          </el-select>
          <span class="top-directions__mode" :class="{'is-global': isGlobal(directionsPlacement)}">
            {{ isGlobal(directionsPlacement) ? 'TOP in all directions' : `TOP in ${directionsPlacement.illness_kind_ids.length} of ${linkedIllnessKindIds.length} directions` }}
          </span>
          <base-button v-if="!isGlobal(directionsPlacement)" type="default" size="sm" :disabled="loading"
                       @click="saveDirections(directionsPlacement, [])">
            Show in all directions
          </base-button>
        </div>
      </template>
      <div class="top-directions__list">
        <div
          v-for="direction in directions"
          :key="direction.id"
          class="top-direction"
          :class="{'is-unlinked': !direction.linked}"
          :title="direction.linked ? '' : 'Not linked to the clinic: the site will not show TOP here'"
        >
          <span class="top-direction__name">{{ direction.name }}</span>
          <label class="top-direction__switch">
            <span>Listing</span>
            <el-switch
              :value="direction.listing === 'in_progress'"
              :disabled="loading"
              active-color="#2dce89"
              inactive-color="#c0c4cc"
              @change="toggleListing(direction)"
            />
            <small v-if="direction.listing === 'suspended'" class="top-direction__note">no balance</small>
          </label>
          <label class="top-direction__switch">
            <span>TOP</span>
            <el-switch
              :value="isDirectionOn(direction.id)"
              :disabled="loading || !directionsPlacement"
              active-color="#2dce89"
              inactive-color="#c0c4cc"
              @change="toggleDirection(direction.id)"
            />
          </label>
          <span v-if="!direction.linked" class="top-direction__warning">not linked</span>
        </div>
      </div>
    </div>
  </card>
</template>

<script>
import APIClinics from '@/api/modules/clinics';
import { mapActions, mapState } from 'vuex';

const KYIV = 'Europe/Kyiv';

export default {
  name: 'ClinicTopPlacements',
  data() {
    return {
      placements: [],
      balanceAds: [],
      loading: false,
      slotLimit: 7,
      // Mirrors TOP_CLINICS_MIN_RATING on the selection side; the card must
      // not promise a bar the block does not apply.
      minRating: 4.3,
      newPeriod: null,
      newWeight: 0,
      newPaymentReference: '',
      newShowOnHomepage: false,
      directionsPlacementId: null,
      editingId: null,
      editingWeight: 0,
    };
  },
  computed: {
    ...mapState('clinics', ['clinic']),
    ...mapState('cmsApiCatalogs', ['illness-kinds']),
    activePeriod() {
      const now = Date.now();

      return this.placements.find((placement) => placement.status === 'confirmed'
        && new Date(placement.starts_at).getTime() <= now
        && new Date(placement.ends_at).getTime() > now) || null;
    },
    /**
     * The same clinic-wide rules the selection applies, answered from the
     * clinic the card already loaded. Per-page rules cannot be answered here,
     * which the note below the list says out loud.
     */
    readiness() {
      const flags = this.clinic.flags || [];
      const flag = (name) => {
        const found = flags.find((item) => item.name === name);

        return found ? found.value : null;
      };
      const rating = (this.clinic.rating || {}).rating || 0;

      return [
        { label: 'Has a confirmed period for today', passed: this.activePeriod !== null },
        { label: 'Logo uploaded', passed: Boolean(this.clinic.logo) },
        {
          label: `Bookimed rating ${rating} of at least ${this.minRating}`,
          passed: rating >= this.minRating,
        },
        { label: 'Not hidden', passed: flag('is_hide') === false },
        { label: 'Active partnership', passed: flag('has_active_agencies') === true },
      ];
    },
    isLive() {
      return this.readiness.every((check) => check.passed);
    },
    /** Periods whose directions can still be changed. */
    editablePlacements() {
      return this.placements.filter((row) => row.status !== 'cancelled' && this.validPlacementId(row));
    },
    directionsPlacement() {
      return this.editablePlacements.find((row) => row.placement_id === this.directionsPlacementId)
        || this.activePeriod
        || this.editablePlacements[0]
        || null;
    },
    linkedIllnessKindIds() {
      return (this.clinic.illness_kind_ids || []).map(Number);
    },
    /**
     * Every clinic × direction pair carries two products: the balance-driven
     * listing advertising and the TOP period. The list is the clinic's linked
     * directions; a direction a period holds without a link is added with a
     * warning, because the site shows TOP only where the clinic is linked.
     */
    directions() {
      const listing = {};
      this.balanceAds
        .filter((ad) => ad.entity === 'illness_kind')
        .forEach((ad) => {
          listing[Number(ad.entity_id)] = ad.status;
        });

      const byId = {};
      const add = (id, linked) => {
        if (!byId[id]) {
          byId[id] = { id, name: this.directionName(id), listing: listing[id] || 'none', linked };
        }
      };
      this.linkedIllnessKindIds.forEach((id) => add(id, true));
      const period = this.directionsPlacement;
      if (period && !this.isGlobal(period)) {
        period.illness_kind_ids.map(Number).forEach((id) => add(id, false));
      }

      return Object.values(byId).sort((a, b) => a.name.localeCompare(b.name));
    },
    canCreate() {
      return !this.loading
        && Array.isArray(this.newPeriod)
        && this.newPeriod.length === 2
        && this.validWeight(this.newWeight)
        && this.newPaymentReference.trim() !== '';
    },
  },
  mounted() {
    this.load();
    this.loadBalanceAds();
  },
  methods: {
    ...mapActions('cmsApiCatalogs', ['getIllnessKinds']),
    load() {
      this.loading = true;

      return APIClinics.getTopPlacements({ clinicId: this.clinic.id })
        .then(({ data }) => {
          this.placements = data.clinic_top_placements || [];
        })
        .finally(() => {
          this.loading = false;
        });
    },
    loadBalanceAds() {
      return Promise.all([
        APIClinics.getBalanceAdvertising({ clinicId: this.clinic.id }),
        this.getIllnessKinds({ perPage: 1500 }),
      ]).then(([response]) => {
        this.balanceAds = (response.data.clinics_advertising || [])
          .slice()
          .sort((a, b) => this.directionLabel(a).localeCompare(this.directionLabel(b)));
      });
    },
    /**
     * Records are keyed by entity id. Directions are the only entity these
     * records use today, so their names are resolved; anything else keeps the
     * raw id rather than pretending to a name it does not have.
     */
    directionLabel(ad) {
      if (ad.entity !== 'illness_kind') {
        return `${ad.entity} #${ad.entity_id}`;
      }

      return this.directionName(ad.entity_id);
    },
    directionName(id) {
      const kinds = this['illness-kinds'] || [];
      const found = kinds.find((kind) => String(kind.id) === String(id));
      if (!found) {
        return `direction #${id}`;
      }

      return (found.name && (found.name.en || found.name.ru)) || `direction #${id}`;
    },
    /**
     * The listing campaign of a direction: running → pause it; paused → start
     * it; suspended (the agency balance ran out) → start only when confirmed;
     * no campaign yet → create it, which also starts it.
     */
    toggleListing(direction) {
      let action = 'start';
      if (direction.listing === 'in_progress') {
        action = 'stop';
      } else if (direction.listing === 'none') {
        action = 'create';
      } else if (direction.listing === 'suspended'
        && !window.confirm(`${direction.name}: the agency balance has run out. Start the listing advertising anyway?`)) {
        return Promise.resolve();
      }

      this.loading = true;

      return APIClinics.switchDirectionAdvertising({ clinicId: this.clinic.id, illnessKindId: direction.id, action })
        .then((response) => {
          const error = response && response.data && response.data.error;
          if (error) {
            alert(`Refused: ${error.code || error}`);
          }

          return this.loadBalanceAds();
        })
        .finally(() => {
          this.loading = false;
        });
    },
    isGlobal(row) {
      return !row || !Array.isArray(row.illness_kind_ids) || row.illness_kind_ids.length === 0;
    },
    /** A global period is on in every direction. */
    isDirectionOn(id) {
      const row = this.directionsPlacement;
      if (!row) return false;
      if (this.isGlobal(row)) return true;

      return row.illness_kind_ids.map(Number).includes(Number(id));
    },
    /**
     * In a global period everything is on, so a click switches that one
     * direction off and keeps the rest of the linked ones on explicitly.
     */
    toggleDirection(id) {
      const row = this.directionsPlacement;
      if (!row) return Promise.resolve();
      const current = this.isGlobal(row) ? this.linkedIllnessKindIds.slice() : row.illness_kind_ids.map(Number);
      const next = current.includes(Number(id))
        ? current.filter((item) => item !== Number(id))
        : current.concat(Number(id));

      return this.saveDirections(row, next);
    },
    saveDirections(row, illnessKindIds) {
      return this.reschedule(row, { illness_kind_ids: illnessKindIds });
    },
    toggleHomepage(row, value) {
      return this.reschedule(row, { show_on_homepage: value });
    },
    /** Keeps the dates and order; the service re-checks the slots of what changed. */
    reschedule(row, changes) {
      if (!this.validPlacementId(row)) return Promise.resolve();
      this.loading = true;
      this.directionsPlacementId = row.placement_id;

      return APIClinics.rescheduleTopPlacement({
        placementId: row.placement_id,
        data: {
          starts_on: this.toKyivIsoDay(row.starts_at),
          ends_on: this.toKyivIsoDay(new Date(new Date(row.ends_at).getTime() - 1)),
          sort_order: row.sort_order,
          ...changes,
        },
      })
        .then((response) => this.afterChange(response))
        .finally(() => {
          this.loading = false;
        });
    },
    validWeight(weight) {
      return Number.isInteger(weight) && weight >= 0 && weight <= 100;
    },
    validPlacementId(row) {
      return row && typeof row.placement_id === 'string'
        && row.placement_id.trim() !== '' && row.placement_id !== 'undefined';
    },
    placementWeight(row) {
      return row.weight == null ? Math.max(0, Math.min(100, 100 - row.sort_order)) : row.weight;
    },
    editWeight(row) {
      if (!this.validPlacementId(row)) return;
      this.editingId = row.placement_id;
      this.editingWeight = this.placementWeight(row);
    },
    cancelWeightEdit() {
      this.editingId = null;
    },
    saveWeight(row) {
      if (!this.validPlacementId(row) || !this.validWeight(this.editingWeight)) {
        return Promise.resolve();
      }

      this.loading = true;

      return APIClinics.rescheduleTopPlacement({
        placementId: row.placement_id,
        data: {
          starts_on: this.toKyivIsoDay(row.starts_at),
          ends_on: this.toKyivIsoDay(new Date(new Date(row.ends_at).getTime() - 1)),
          sort_order: row.sort_order,
          weight: this.editingWeight,
        },
      })
        .then((response) => this.afterChange(response))
        .finally(() => {
          this.loading = false;
        });
    },
    create() {
      this.loading = true;

      return APIClinics.createTopPlacement({
        clinicId: this.clinic.id,
        data: {
          starts_on: this.newPeriod[0],
          ends_on: this.newPeriod[1],
          sort_order: 0,
          weight: this.newWeight,
          payment_reference: this.newPaymentReference.trim(),
          show_on_homepage: this.newShowOnHomepage,
        },
      })
        .then((response) => this.afterChange(response))
        .finally(() => {
          this.loading = false;
        });
    },
    act(action, row) {
      if (!this.validPlacementId(row)) return Promise.resolve();
      this.loading = true;

      return APIClinics.changeTopPlacement({ placementId: row.placement_id, action })
        .then((response) => this.afterChange(response))
        .finally(() => {
          this.loading = false;
        });
    },
    afterChange(response) {
      const error = response && response.data && response.data.error;
      if (error) {
        // the service refuses overlaps, a full window and a resume after the
        // end date — the reason is worth showing rather than swallowing
        alert(`Refused: ${error.code || error}`);
        return this.load();
      }

      this.newPeriod = null;
      this.newPaymentReference = '';
      this.newShowOnHomepage = false;
      this.editingId = null;

      return this.load();
    },
    /**
     * The service stores a half-open UTC interval; the manager thinks in
     * inclusive Kyiv days, so the last day is the day before the end.
     */
    periodLabel(row) {
      const first = this.toKyivDay(row.starts_at);
      const last = this.toKyivDay(new Date(new Date(row.ends_at).getTime() - 1));

      return `${first} — ${last}`;
    },
    toKyivDay(value) {
      if (!value) {
        return '—';
      }

      return new Date(value).toLocaleDateString('en-GB', { timeZone: KYIV });
    },
    toKyivIsoDay(value) {
      const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: KYIV, year: 'numeric', month: '2-digit', day: '2-digit',
      }).formatToParts(new Date(value));
      const part = (type) => parts.find((item) => item.type === type).value;

      return `${part('year')}-${part('month')}-${part('day')}`;
    },
    statusType(status) {
      if (status === 'confirmed') {
        return 'success';
      }
      if (status === 'paused') {
        return 'warning';
      }
      if (status === 'cancelled') {
        return 'danger';
      }

      return 'info';
    },
  },
};
</script>

<style lang="scss" scoped>
.top-directions {
  margin-top: 20px;

  &__title {
    font-weight: 600;
    margin-bottom: 4px;
  }

  &__hint,
  &__empty {
    font-size: 13px;
    color: #8898aa;
    margin-bottom: 10px;
  }

  &__period {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
    margin-bottom: 12px;
  }

  &__select {
    min-width: 260px;
  }

  &__mode {
    font-size: 13px;
    font-weight: 600;
    color: #5e72e4;

    &.is-global {
      color: #2dce89;
    }
  }

  &__list {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }
}

.top-direction {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border: 1px solid #dcdfe6;
  border-radius: 6px;
  background: #fff;
  font-size: 12px;

  &__name {
    font-weight: 600;
  }

  &__switch {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    margin: 0;
    color: #8898aa;
    cursor: pointer;
  }

  &__note {
    color: #fb6340;
  }

  &__warning {
    color: #f5365c;
    font-weight: 600;
  }

  &.is-unlinked {
    border-style: dashed;
    border-color: #f5365c;
  }
}

.top-placements__all {
  color: #2dce89;
}

.balance-ads {
  margin-top: 20px;
  padding-top: 15px;
  border-top: 1px solid #dfe1e5;

  &__title {
    font-size: 14px;
    font-weight: 600;
    margin: 0 0 4px;
  }

  &__hint {
    font-size: 12px;
    line-height: 140%;
    color: rgba(0, 0, 0, 0.38);
    margin-bottom: 10px;
  }

  &__empty {
    font-size: 13px;
    color: rgba(0, 0, 0, 0.38);
  }

  &__list {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }
}

.top-placements {
  &__now {
    border: 1px solid #dfe1e5;
    border-left: 3px solid #f5a623;
    border-radius: 3px;
    padding: 12px 15px;
    margin-bottom: 15px;

    &--live {
      border-left-color: #a3cc0e;

      .top-placements__now-dot {
        background: #a3cc0e;
      }
    }

    &-state {
      display: flex;
      align-items: center;
      font-weight: 600;
      font-size: 14px;
    }

    &-dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: #f5a623;
      margin-right: 8px;
    }

    &-period {
      font-size: 13px;
      color: rgba(0, 0, 0, 0.56);
      margin-top: 4px;
    }

    &-note {
      font-size: 12px;
      line-height: 140%;
      color: rgba(0, 0, 0, 0.38);
      margin: 8px 0 0;
    }
  }

  &__checks {
    list-style: none;
    padding: 0;
    margin: 10px 0 0;
    display: flex;
    flex-wrap: wrap;
    gap: 4px 18px;
    font-size: 13px;
    color: rgba(0, 0, 0, 0.56);

    .is-failed {
      color: #ec250d;
      font-weight: 600;
    }
  }

  &__hint {
    font-size: 13px;
    line-height: 140%;
    color: rgba(0, 0, 0, 0.38);
    margin-bottom: 15px;
  }

  &__form {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
    margin-bottom: 15px;
  }

  &__reference {
    width: 220px;
  }

  &__weight-label {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    white-space: nowrap;
  }
}
</style>
